import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Loader2, ArrowLeft } from 'lucide-react';
import { ArticleCard } from '@/components/ArticleCard';
import { SEOHead } from '@/components/SEOHead';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categoryLabels = {
  actualites: 'Actualités',
  histoire: 'Histoire',
  culture: 'Culture',
  politique: 'Politique',
  sport: 'Sport',
  justice: 'Justice'
};

const categoryDescriptions = {
  actualites: 'Les dernières nouvelles de Versailles et ses environs',
  histoire: 'Découvrez l\'histoire riche de Versailles et du château',
  culture: 'Événements culturels, expositions et vie artistique',
  politique: 'La vie politique locale et les décisions municipales',
  sport: 'Actualités sportives et événements de la région',
  justice: 'Actualités judiciaires de la cour d\'appel de Versailles'
};

export default function CategoryPage() {
  const { category } = useParams();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API_URL}/articles?category=${category}&limit=50`);
        setArticles(response.data);
      } catch (error) {
        console.error('Error fetching articles:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchArticles();
    window.scrollTo(0, 0);
  }, [category]);

  const categoryName = categoryLabels[category] || category;
  const categoryDescription = categoryDescriptions[category] || '';

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="category-loading">
        <Loader2 className="h-8 w-8 animate-spin text-red-800" />
      </div>
    );
  }

  return (
    <>
      <SEOHead 
        title={`${categoryLabels[category] || category} | VersaillesPlus`}
        description={categoryDescriptions[category] || `Actualités ${category} de Versailles et des Yvelines`}
      />
      <Header />
      <div className="bg-stone-100 min-h-screen" data-testid="category-page">
      {/* Header */}
      <section className="bg-white border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Link 
            to="/"
            className="inline-flex items-center gap-2 font-inter text-sm text-stone-500 hover:text-red-800 transition-colors duration-200 mb-6"
            data-testid="category-back-home"
          >
            <ArrowLeft className="h-4 w-4" />
            Accueil
          </Link>
          
          <h1 
            className="font-playfair text-4xl sm:text-5xl font-bold text-stone-900 mb-4"
            data-testid="category-title"
          >
            {categoryName}
          </h1>
          
          <p className="font-lora text-lg text-stone-600 max-w-2xl">
            {categoryDescription}
          </p>
        </div>
      </section>

      {/* Articles Grid */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {articles.length > 0 ? (
          <>
            {/* Featured article */}
            {articles[0] && (
              <div className="mb-12">
                <ArticleCard article={articles[0]} variant="hero" />
              </div>
            )}

            {/* Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-10">
              {articles.slice(1).map((article, index) => (
                <div 
                  key={article.id}
                  className={`${index % 3 !== 2 ? 'lg:border-r lg:border-stone-200 lg:pr-8' : ''}`}
                >
                  <ArticleCard article={article} />
                </div>
              ))}
            </div>
          </>
        ) : (
          <div className="text-center py-20">
            <h2 className="font-playfair text-2xl font-bold text-stone-900 mb-4">
              Aucun article dans cette catégorie
            </h2>
            <p className="font-lora text-stone-600 mb-8">
              Les articles de la catégorie {categoryName} arrivent bientôt.
            </p>
            <Link 
              to="/"
              className="inline-flex items-center gap-2 font-inter text-red-800 hover:text-red-900"
            >
              <ArrowLeft className="h-4 w-4" />
              Retour à l'accueil
            </Link>
          </div>
        )}
      </section>
    </div>
    <Footer />
    </>
  );
}
